import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CheckIcon from '@mui/icons-material/Check';
import { useQualifyingAnswers, useVideoAnswer } from '../../hooks';
import { formatRecordingTime } from '../../functions';
import type {
  IQualifyingQuestionsProps,
  IVideoAnswerInputProps,
} from '../../interfaces';
import { LocationPicker } from './LocationPicker';

export const VideoAnswerInput = ({ questionId, onCapture }: IVideoAnswerInputProps) => {
  const recorder = useVideoAnswer({ questionId, onCapture });

  return (
    <Stack spacing={1.5} sx={{ maxWidth: 448, mx: 'auto' }}>
      <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'common.black',
            overflow: 'hidden',
            aspectRatio: '16 / 9',
          }}
      >
        {recorder.showPlayback && (
          <Box component="video" key={recorder.previewUrl} src={recorder.previewUrl ?? undefined} controls sx={{ width: '100%', height: '100%', objectFit: 'contain', bgcolor: 'common.black' }} />
        )}

        {recorder.showLiveCamera && (
          <Box
              component="video"
              ref={recorder.videoRef}
              autoPlay
              playsInline
              muted
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {recorder.mode === 'processing' && (
          <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0, bgcolor: 'common.black' }}>
            <CircularProgress size={32} sx={{ color: 'common.white' }} />
            <Typography variant="caption" sx={{ color: 'common.white', opacity: 0.6 }}>
              Processing recording…
            </Typography>
          </Stack>
        )}

        {recorder.mode === 'idle' && !recorder.previewUrl && (
          <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0, color: 'grey.500' }}>
            <VideocamIcon fontSize="large" sx={{ opacity: 0.4 }} />
            <Typography variant="caption" color="text.secondary">Record or upload a video</Typography>
          </Stack>
        )}

        {recorder.mode === 'recording' && (
          <Chip
            icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'common.white', ml: 1 }} />}
            label={`REC ${formatRecordingTime(recorder.recordingSecs)}`}
            size="small"
            color="error"
            sx={{ position: 'absolute', top: 8, left: 8, fontWeight: 700, fontFamily: 'monospace' }}
          />
        )}
      </Box>

      {recorder.cameraError && (
        <Alert severity="warning">{recorder.cameraError}</Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        {recorder.showPlayback ? (
          <>
            <Button variant="contained" size="small" onClick={recorder.handleRecordAgain}>
              Record Again
            </Button>
            <Button variant="outlined" size="small" onClick={recorder.triggerFilePicker}>
              Upload Instead
            </Button>
          </>
        ) : recorder.mode === 'camera' ? (
          <Button
              variant="contained"
              size="small"
              color="error"
              startIcon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'common.white' }} />}
              onClick={recorder.startRecording}
          >
            Start Recording
          </Button>
        ) : recorder.mode === 'recording' ? (
          <Button
              variant="contained"
              size="small"
              color="error"
              startIcon={<Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'common.white' }} />}
              onClick={recorder.stopRecording}
          >
            Stop Recording
          </Button>
        ) : recorder.mode === 'processing' ? (
          <Button variant="contained" size="small" disabled>
            Processing…
          </Button>
        ) : (
          <>
            <Button variant="contained" size="small" onClick={() => void recorder.openCamera()}>
              Record Video
            </Button>
            <Button variant="outlined" size="small" onClick={recorder.triggerFilePicker}>
              Upload Video
            </Button>
          </>
        )}
        <input
            ref={recorder.fileInputRef}
            type="file"
            accept="video/*"
            hidden
            onChange={(e) => recorder.handleFileChange(e.target.files?.[0] ?? null)}
        />
      </Stack>
    </Stack>
  );
};

export const QualifyingQuestions = ({ questions, currentStep, onComplete }: IQualifyingQuestionsProps) => {
  const qualifying = useQualifyingAnswers({ questions, currentStep, onComplete });
  const currentQuestion = qualifying.currentQuestion;

  if (!currentQuestion) return null;

  const currentValue = qualifying.getAnswerValue(currentQuestion.questionId).toString();

  return (
    <Box sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 4 }}>
        {questions.map((question, index) => (
          <React.Fragment key={question.questionId}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: index === currentStep ? 'primary.main' : index < currentStep ? 'primary.50' : 'background.paper',
                color: index === currentStep ? 'primary.contrastText' : index < currentStep ? 'primary.main' : 'text.disabled',
                border: 2,
                borderColor: index === currentStep ? 'primary.main' : index < currentStep ? 'primary.50' : 'divider',
              }}
            >
              {index < currentStep ? <CheckIcon fontSize="small" /> : index + 1}
            </Avatar>
            {index < questions.length - 1 && (
              <Box
                  sx={{
                    width: 32,
                    height: 2,
                    bgcolor: index < currentStep ? 'primary.main' : 'divider',
                  }}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>

      <Box key={currentStep}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="primary" fontWeight={600} sx={{ textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
            Question {currentStep + 1} of {questions.length}
          </Typography>
          <Typography variant="h3" sx={{ maxWidth: 400, mx: 'auto' }}>
            {currentQuestion.question}
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ bgcolor: 'grey.100', p: 3 }}>
          {currentQuestion.questionType === 'multiple-choice' ? (
            <Grid container spacing={1.5} sx={{ maxWidth: 340, mx: 'auto' }}>
              {currentQuestion.choices?.map((choice) => {
                const selected = currentValue === choice.toString();
                return (
                  <Grid item xs={12} key={choice}>
                    <Paper
                        variant="outlined"
                        role="radio"
                        aria-checked={selected}
                        tabIndex={0}
                        onClick={() => qualifying.handleChangeAnswer(currentQuestion.questionId, currentQuestion.questionType, choice)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            qualifying.handleChangeAnswer(currentQuestion.questionId, currentQuestion.questionType, choice);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          cursor: 'pointer',
                          borderWidth: 2,
                          borderColor: selected ? 'primary.main' : 'divider',
                        }}
                    >
                      <Avatar sx={{ width: 16, height: 16, bgcolor: selected ? 'primary.main' : 'transparent', border: 2, borderColor: selected ? 'primary.main' : 'divider' }}>
                        {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'common.white' }} />}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600} color={selected ? 'text.primary' : 'text.secondary'}>
                        {choice}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : currentQuestion.questionType === 'location' ? (
            <Box sx={{ maxWidth: 440, mx: 'auto', py: 1 }}>
              <LocationPicker
                  value={currentValue}
                  onChange={(value, data) => qualifying.handleChangeAnswer(currentQuestion.questionId, 'location', value, data)}
              />
            </Box>
          ) : currentQuestion.questionType === 'text' ? (
            <Box sx={{ maxWidth: 400, mx: 'auto', py: 1 }}>
              <TextField
                  value={currentValue}
                  placeholder="Type your answer here..."
                  onChange={(e) => qualifying.handleChangeAnswer(currentQuestion.questionId, currentQuestion.questionType, e.target.value)}
                  fullWidth
                  multiline
                  variant="standard"
                  inputProps={{ 'aria-label': currentQuestion.question, style: { textAlign: 'center', fontSize: '1.125rem' } }}
              />
            </Box>
          ) : currentQuestion.questionType === 'video' ? (
            <VideoAnswerInput questionId={currentQuestion.questionId} onCapture={qualifying.handleVideoCapture} />
          ) : (
            <Box sx={{ maxWidth: 280, mx: 'auto', py: 1 }}>
              <TextField
                  value={currentValue}
                  type="number"
                  placeholder="0"
                  onChange={(e) => qualifying.handleChangeAnswer(currentQuestion.questionId, 'numeric', e.target.value)}
                  fullWidth
                  variant="standard"
                  inputProps={{ 'aria-label': currentQuestion.question, style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 700 } }}
              />
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', display: 'block', textAlign: 'center', mt: 1.5 }}>
                Numerical response only
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
